import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CheckCircle2, AlertTriangle, Info, X } from "lucide-react";

// Lightweight app-wide toast system. One <ToastHost /> mounts the
// portal area in any layout; anywhere in the tree calls `useToast()`
// to get { success(msg), error(msg), info(msg), dismiss(id) }.
//
// Was added so the various inline "Saved" / "Failed" feedback blocks
// scattered across BBP, MatchControl, Squad, etc. can converge on a
// single consistent surface instead of re-inventing per page.

const ToastCtx = createContext(null);

let counter = 1;

export function ToastHost({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((kind, msg, opts = {}) => {
    const id = counter++;
    const ttl = opts.ttl ?? (kind === "error" ? 7000 : 3500);
    setToasts((prev) => [...prev, { id, kind, msg, ...opts }]);
    if (ttl > 0) setTimeout(() => dismiss(id), ttl);
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({
    success: (msg, opts) => push("success", msg, opts),
    error: (msg, opts) => push("error", msg, opts),
    info: (msg, opts) => push("info", msg, opts),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </ToastCtx.Provider>
  );
}

function ToastViewport({ toasts, dismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="fixed top-3 right-3 z-[100] flex flex-col gap-2 max-w-sm pointer-events-none">
      {toasts.map((t) => <ToastItem key={t.id} toast={t} onClose={() => dismiss(t.id)} />)}
    </div>
  );
}

function ToastItem({ toast, onClose }) {
  const { kind, msg } = toast;
  const palette = {
    success: { Icon: CheckCircle2, cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" },
    error:   { Icon: AlertTriangle, cls: "bg-rose-500/15 border-rose-500/40 text-rose-200" },
    info:    { Icon: Info,           cls: "bg-blue-500/15 border-blue-500/40 text-blue-200" },
  }[kind] || { Icon: Info, cls: "bg-bg-secondary border-border-default text-text-primary" };
  return (
    <div className={`pointer-events-auto flex items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-xl backdrop-blur-sm ${palette.cls}`}>
      <palette.Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <span className="flex-1 leading-snug">{msg}</span>
      <button onClick={onClose} className="opacity-70 hover:opacity-100" aria-label="Dismiss">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  // Soft fallback so a component used outside ToastHost doesn't crash —
  // useful in unit tests / story-isolation. Production layouts always
  // mount the host so the real implementation runs.
  if (!ctx) {
    return {
      success: (m) => console.info("[toast]", m),
      error: (m) => console.warn("[toast]", m),
      info: (m) => console.log("[toast]", m),
      dismiss: () => {},
    };
  }
  return ctx;
}
